import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import {
  DomainTopicsService,
  ParentTopic,
  TopicLink,
  TopicGraphNode,
  TopicGraphEdge,
  TopicTreeResponse,
  TopicTreeNode,
  TopicOverviewNode,
  TopicOverviewEdge,
  DiagnosticResponse,
  DiagnosticTopicUrl
} from '../../services/domain-topics';
import { FeatureFlagService } from '../../services/feature-flag';
import { TopicDiagnosticComponent } from '../topic-diagnostic/topic-diagnostic';
import { environment } from '../../../environments/environment';
import { DomainContextService } from '../../services/domain-context';
import { Subscription } from 'rxjs';

interface TreeTopicEntry {
  topic: ParentTopic;
  data: DiagnosticResponse['data'] | null;
  loading: boolean;
  error: string | null;
}

interface TreeSubtopicNode {
  parentTopicSlug: string;
  label: string;
  pageCount: number;
  coreCount: number;
  mentionCount: number;
  corePages: DiagnosticTopicUrl[];
  mentionPages: DiagnosticTopicUrl[];
}

@Component({
  selector: 'app-domain-topics',
  standalone: true,
  imports: [CommonModule, FormsModule, TopicDiagnosticComponent],
  templateUrl: './domain-topics.html',
  styleUrl: './domain-topics.css'
})
export class DomainTopicsComponent implements OnInit, OnDestroy {
  domain: string = '';
  topics: ParentTopic[] = [];
  selectedTopic: ParentTopic | null = null;
  loading = false;
  error: string | null = null;
  topicLinks: TopicLink[] = [];
  structuralLinks: TopicLink[] = [];
  loadingLinks = false;
  showLinks = false;
  
  // Graph visualization
  viewMode: 'list' | 'graph' | 'tree' | 'old-user-list' = 'list';
  graphNodes: TopicGraphNode[] = [];
  graphEdges: TopicGraphEdge[] = [];
  loadingGraph = false;
  graphError: string | null = null;
  minSimilarity = 0.65;
  graphDataSource: 'legacy' | 'overview' = 'overview';
  topN = 40;
  minGraphAuthority = 0;
  minGraphConfidence = 0.6;
  maxEdgesPerNode = 12;
  graphSearch = '';
  
  // Side panel for topic tree
  selectedTopicForTree: TopicGraphNode | null = null;
  topicTree: TopicTreeResponse | null = null;
  loadingTree = false;
  treeError: string | null = null;
  showSidePanel = false;

  showDiagnostic = false;
  diagnosticTopic: ParentTopic | null = null;
  treeEntries: TreeTopicEntry[] = [];
  loadingTreeView = false;
  treeViewError: string | null = null;
  treeTagLabelMode: 'variant' | 'canonical' = 'variant';
  expandedSubtopics = new Set<string>();
  mentionDisplayCount = new Map<string, number>();

  // Share link
  private readonly API_URL = environment.apiUrl + '/v1';
  shareLoading = false;
  shareUrl: string | null = null;
  shareCopied = false;
  shareError: string | null = null;
  private readonly subscriptions = new Subscription();

  constructor(
    private domainTopicsService: DomainTopicsService,
    private http: HttpClient,
    public featureFlags: FeatureFlagService,
    private domainContext: DomainContextService
  ) {}

  ngOnInit() {
    this.domainContext.ensureInitialized();
    this.subscriptions.add(this.domainContext.selectedDomain$.subscribe((domain) => {
      if (!domain || domain === this.domain) return;
      this.domain = domain;
      this.graphNodes = [];
      this.graphEdges = [];
      this.topicTree = null;
      this.treeEntries = [];
      this.selectedTopic = null;
      this.showLinks = false;
      this.topicLinks = [];
      this.structuralLinks = [];
      this.shareUrl = null;
      this.shareError = null;
      this.loadTopics();
    }));

    if (this.featureFlags.isUserView()) {
      this.viewMode = 'tree';
    }
  }

  loadGraph() {
    if (!this.domain.trim()) {
      this.graphError = 'Please enter a domain';
      return;
    }

    this.loadingGraph = true;
    this.graphError = null;
    this.graphNodes = [];
    this.graphEdges = [];

    if (this.graphDataSource === 'overview') {
      this.loadOverviewGraph();
      return;
    }

    this.domainTopicsService.getTopicGraph(this.domain, this.minSimilarity).subscribe({
      next: (response) => {
        this.loadingGraph = false;
        if (response.success && response.data) {
          this.graphNodes = response.data.nodes || [];
          this.graphEdges = response.data.edges || [];
          
          // Debug: Check if paths are in the data
          const nodesWithPaths = this.graphNodes.filter(n => n.pathToGrandparent && n.pathToGrandparent.length > 0);
          console.log(`📊 Graph loaded: ${this.graphNodes.length} nodes, ${this.graphEdges.length} edges`);
          console.log(`📊 Nodes with transitive paths: ${nodesWithPaths.length}`);
          if (nodesWithPaths.length > 0) {
            console.log('📊 Example paths:', nodesWithPaths.slice(0, 3).map(n => ({
              topic: n.id,
              grandparent: n.grandparentTopic,
              path: n.pathToGrandparent
            })));
          }
          
          // Check specifically for user-acceptance-testing
          const uatNode = this.graphNodes.find(n => n.id === 'user-acceptance-testing');
          if (uatNode) {
            console.log('📊 user-acceptance-testing node:', {
              id: uatNode.id,
              grandparent: uatNode.grandparentTopic,
              pathToGrandparent: uatNode.pathToGrandparent,
              hasPath: !!(uatNode.pathToGrandparent && uatNode.pathToGrandparent.length > 0)
            });
          }
          
          if (this.graphNodes.length === 0) {
            this.graphError = 'No topics found for this domain. Run aggregation first.';
          }
        } else {
          this.graphError = 'Failed to load topic graph';
        }
      },
      error: (err) => {
        this.loadingGraph = false;
        this.graphError = err.error?.message || err.error?.error || 'Failed to load topic graph. Make sure topics are aggregated first.';
        console.error('Error loading graph:', err);
      }
    });
  }

  private loadOverviewGraph() {
    this.domainTopicsService.getTopicOverview(this.domain, {
      topN: this.topN,
      minAuthority: this.minGraphAuthority,
      minConfidence: this.minGraphConfidence,
      maxEdgesPerNode: this.maxEdgesPerNode,
      search: this.graphSearch.trim() || undefined
    }).subscribe({
      next: (response) => {
        this.loadingGraph = false;
        if (response.success && response.data) {
          const overviewNodes = response.data.nodes || [];
          const overviewEdges = response.data.edges || [];
          this.graphNodes = overviewNodes.map((node: TopicOverviewNode): TopicGraphNode => ({
            id: node.topicSlug,
            label: node.label,
            type: 'parentTopic',
            grandparentTopic: node.community,
            pageCount: node.pageCount,
            authorityScore: node.authorityScore,
            exists: true
          }));
          this.graphEdges = overviewEdges.map((edge: TopicOverviewEdge): TopicGraphEdge => ({
            source: edge.sourceTopicSlug,
            target: edge.targetTopicSlug,
            similarity: edge.similarity,
            type: edge.edgeType
          }));
          if (this.graphNodes.length === 0) {
            this.graphError = 'No topics found for this domain. Run aggregation first.';
          }
        } else {
          this.graphError = 'Failed to load overview graph';
        }
      },
      error: (err) => {
        this.loadingGraph = false;
        this.graphError = err.error?.message || err.error?.error || 'Failed to load overview graph.';
      }
    });
  }

  switchView(mode: 'list' | 'graph' | 'tree' | 'old-user-list') {
    this.viewMode = mode;
    if (mode === 'graph' && this.graphNodes.length === 0 && !this.loadingGraph) {
      this.loadGraph();
    }
    if (mode === 'tree' && this.treeEntries.length === 0 && !this.loadingTreeView) {
      this.loadTreeView();
    }
  }

  getEdgeColor(edge: TopicGraphEdge): string {
    if (edge.type === 'merged') return '#4CAF50'; // Green
    if (edge.type === 'related') return '#FF9800'; // Orange
    return '#9E9E9E'; // Gray
  }

  getNodeColor(node: TopicGraphNode): string {
    if (node.authorityScore >= 70) return '#4CAF50'; // Green
    if (node.authorityScore >= 40) return '#FF9800'; // Orange
    return '#9E9E9E'; // Gray
  }

  getGrandparentForEdge(edge: TopicGraphEdge): string | null {
    // Find the grandparent topic for this edge
    // If both topics are in the same grandparentTopic, return that
    const sourceNode = this.graphNodes.find(n => n.id === edge.source);
    const targetNode = this.graphNodes.find(n => n.id === edge.target);
    
    if (!sourceNode || !targetNode) return null;
    
    // If they share a grandparent, return that
    if (sourceNode.grandparentTopic && targetNode.grandparentTopic) {
      if (sourceNode.grandparentTopic === targetNode.grandparentTopic) {
        return sourceNode.grandparentTopic;
      }
    }
    
    // If edge type is 'merged', check which one is the grandparent
    if (edge.type === 'merged') {
      // If source's grandparent is itself, source is the grandparent
      if (sourceNode.grandparentTopic === edge.source) {
        return edge.source;
      }
      // If target's grandparent is itself, target is the grandparent
      if (targetNode.grandparentTopic === edge.target) {
        return edge.target;
      }
      // If they share a grandparent, return that
      if (sourceNode.grandparentTopic && targetNode.grandparentTopic && 
          sourceNode.grandparentTopic === targetNode.grandparentTopic) {
        return sourceNode.grandparentTopic;
      }
    }
    
    return null;
  }

  loadTopics() {
    if (!this.domain.trim()) {
      this.error = 'Please enter a domain';
      return;
    }

    this.loading = true;
    this.error = null;
    this.selectedTopic = null;

    this.domainTopicsService.getDomainTopics(this.domain).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.success && response.data.parentTopics) {
          this.topics = response.data.parentTopics;
          if (this.viewMode === 'graph') {
            this.loadGraph();
          }
          if (this.viewMode === 'tree') {
            this.loadTreeView();
          }
          if (this.topics.length === 0) {
            this.error = response.data.message || 'No topics found for this domain. Run aggregation first.';
          }
        } else {
          this.error = response.data.message || 'Failed to load topics';
        }
      },
      error: (err) => {
        this.loading = false;
        this.error = err.error?.message || err.message || 'Failed to load topics';
        console.error('Error loading topics:', err);
      }
    });
  }

  selectTopic(topic: ParentTopic) {
    if (this.selectedTopic === topic) {
      this.selectedTopic = null;
      this.showLinks = false;
      this.topicLinks = [];
      this.structuralLinks = [];
    } else {
      this.selectedTopic = topic;
      this.showLinks = false;
      this.topicLinks = [];
      this.structuralLinks = [];
    }
  }

  getScoreColor(score: number): string {
    if (score >= 80) return '#10b981'; // green
    if (score >= 60) return '#3b82f6'; // blue
    if (score >= 40) return '#f59e0b'; // orange
    return '#ef4444'; // red
  }

  /**
   * Get tier badge styling
   */
  getTierBadgeClass(tier?: string): string {
    switch (tier) {
      case 'owned': return 'tier-badge tier-owned';
      case 'aspirational': return 'tier-badge tier-aspirational';
      case 'ambient': return 'tier-badge tier-ambient';
      case 'adjacent': return 'tier-badge tier-adjacent';
      case 'anti': return 'tier-badge tier-anti';
      default: return 'tier-badge tier-unclassified';
    }
  }

  /**
   * Get tier label
   */
  getTierLabel(tier?: string): string {
    switch (tier) {
      case 'owned': return '🏆 Owned';
      case 'aspirational': return '📈 Aspirational';
      case 'ambient': return '💬 Ambient';
      case 'adjacent': return '🔗 Adjacent';
      case 'anti': return '🚫 Filtered';
      default: return '❓ Unclassified';
    }
  }

  /**
   * Get tier description
   */
  getTierDescription(tier?: string): string {
    switch (tier) {
      case 'owned': return 'Strategic core topic - high authority, coverage, and focus';
      case 'aspirational': return 'Growing topic with good per-page quality';
      case 'ambient': return 'Mentioned frequently but not strategic (broad but shallow)';
      case 'adjacent': return 'Supporting topic, exists but not a strategic target';
      case 'anti': return 'Filtered out (brand mention or link-inflated)';
      default: return 'Does not match any classification tier';
    }
  }

  getScoreBarWidth(score: number): string {
    return `${Math.min(score, 100)}%`;
  }

  /**
   * Check if topic has boilerplate content penalty applied
   */
  hasBoilerplatePenalty(topic: ParentTopic): boolean {
    if (!topic.effectivePageCountForCoverage) {
      return false;
    }
    // If effective count is significantly less than actual count, penalty was applied
    return topic.effectivePageCountForCoverage < topic.pageCount * 0.5;
  }

  /**
   * Get the effective page count to display (or actual count if no penalty)
   */
  getDisplayPageCount(topic: ParentTopic): number {
    if (this.hasBoilerplatePenalty(topic) && topic.effectivePageCountForCoverage) {
      return Math.round(topic.effectivePageCountForCoverage);
    }
    return topic.pageCount;
  }

  /**
   * Get tooltip text explaining boilerplate penalty
   */
  getBoilerplateTooltip(topic: ParentTopic): string {
    if (!this.hasBoilerplatePenalty(topic) || !topic.effectivePageCountForCoverage) {
      return '';
    }
    const boilerplatePages = topic.pageCount - Math.round(topic.effectivePageCountForCoverage);
    return `This topic appears on ${topic.pageCount} pages, but ${boilerplatePages} pages contain boilerplate content (repeated FAQ blocks, static text). The effective coverage is ${Math.round(topic.effectivePageCountForCoverage)} pages after discounting boilerplate content.`;
  }

  /**
   * Check if topic has link score data
   * Phase 2: Internal Links
   */
  hasLinkScore(topic: ParentTopic): boolean {
    return topic.linkScore !== undefined && topic.linkScore > 0;
  }

  /**
   * Get link explanation text
   * Phase 2: Internal Links
   */
  getLinkExplanation(topic: ParentTopic): string {
    if (!topic.authorityExplanation?.links) {
      return '';
    }
    return topic.authorityExplanation.links;
  }

  /**
   * Load link details for selected topic
   * Phase 2: Internal Links
   */
  loadTopicLinks() {
    if (!this.selectedTopic || !this.domain) {
      return;
    }

    this.loadingLinks = true;
    this.showLinks = true;
    this.topicLinks = [];
    this.structuralLinks = [];

    this.domainTopicsService.getTopicLinks(this.domain, this.selectedTopic.parentTopicSlug).subscribe({
      next: (response) => {
        this.loadingLinks = false;
        if (response.success && response.data) {
          this.topicLinks = response.data.contextualLinks || [];
          this.structuralLinks = response.data.structuralLinks || [];
        }
      },
      error: (err) => {
        this.loadingLinks = false;
        console.error('Error loading topic links:', err);
      }
    });
  }

  /**
   * Toggle link details visibility
   */
  toggleLinks() {
    if (!this.showLinks && this.selectedTopic) {
      this.loadTopicLinks();
    } else {
      this.showLinks = false;
    }
  }
  
  openTopicTree(node: TopicGraphNode) {
    this.selectedTopicForTree = node;
    this.showSidePanel = true;
    this.loadingTree = true;
    this.treeError = null;
    this.topicTree = null;
    
    this.domainTopicsService.getTopicTree(this.domain, node.id, 3, 5).subscribe({
      next: (response) => {
        this.loadingTree = false;
        if (response.success && response.data) {
          this.topicTree = response;
        } else {
          this.treeError = 'Failed to load topic tree';
        }
      },
      error: (err) => {
        this.loadingTree = false;
        this.treeError = err.error?.message || err.error?.error || 'Failed to load topic tree';
        console.error('Error loading topic tree:', err);
      }
    });
  }
  
  closeSidePanel() {
    this.showSidePanel = false;
    this.selectedTopicForTree = null;
    this.topicTree = null;
  }
  
  getRelationshipColor(type?: string): string {
    switch (type) {
      case 'merged': return '#4CAF50'; // Green
      case 'related': return '#2196F3'; // Blue
      case 'somewhat-related': return '#FF9800'; // Orange
      case 'grandparent': return '#9C27B0'; // Purple
      case 'bridge': return '#00BCD4'; // Cyan
      default: return '#9E9E9E'; // Gray
    }
  }
  
  getRelationshipLabel(type?: string): string {
    switch (type) {
      case 'merged': return 'Merged';
      case 'related': return 'Related';
      case 'somewhat-related': return 'Somewhat Related';
      case 'grandparent': return 'Grandparent';
      case 'bridge': return 'Bridge';
      default: return 'Unknown';
    }
  }

  /**
   * Get filtered topics for User View
   * Filters topics with authority > 30 AND tier in ['owned', 'aspirational', 'adjacent']
   * Limits to top 2-3 topics
   */
  get oldUserListTopics(): ParentTopic[] {
    const filtered = this.topics.filter(topic => {
      const hasAuthority = topic.authorityScore > 30;
      const hasValidTier = topic.topicTier && ['owned', 'aspirational', 'adjacent'].includes(topic.topicTier);
      return hasAuthority && hasValidTier;
    });

    return filtered.sort((a, b) => b.authorityScore - a.authorityScore).slice(0, 3);
  }

  /**
   * Open diagnostic view for a specific topic
   */
  openDiagnostic(topic: ParentTopic) {
    this.diagnosticTopic = topic;
    this.showDiagnostic = true;
  }

  /**
   * Close diagnostic view
   */
  closeDiagnostic() {
    this.showDiagnostic = false;
    this.diagnosticTopic = null;
  }

  loadTreeView() {
    this.treeViewError = null;
    const topTopics = this.getTreeEligibleTopics().slice(0, 3);

    if (topTopics.length === 0) {
      this.treeEntries = [];
      return;
    }

    this.treeEntries = topTopics.map(topic => ({
      topic,
      data: null,
      loading: true,
      error: null
    }));
    this.expandedSubtopics.clear();
    this.mentionDisplayCount.clear();
    this.loadingTreeView = true;

    const loadNext = (index: number) => {
      if (index >= this.treeEntries.length) {
        this.loadingTreeView = false;
        return;
      }
      const entry = this.treeEntries[index];
      this.domainTopicsService.getTopicDiagnostic(this.domain, entry.topic.parentTopicSlug).subscribe({
        next: (response) => {
          entry.loading = false;
          if (response.success && response.data) {
            entry.data = response.data;
          } else {
            entry.error = 'Failed to load topic coverage tree.';
          }
          loadNext(index + 1);
        },
        error: (err) => {
          entry.loading = false;
          entry.error = err.error?.message || err.error?.error || 'Failed to load topic coverage tree.';
          loadNext(index + 1);
        }
      });
    };
    loadNext(0);
  }

  getDirectSubtopics(entry: TreeTopicEntry): TreeSubtopicNode[] {
    const subtopics = entry.data?.coverage?.directlyCoveredTopics || [];
    return subtopics
      .slice()
      .sort((a, b) => b.pageCount - a.pageCount);
  }

  getRelatedSubtopics(entry: TreeTopicEntry): TreeSubtopicNode[] {
    const subtopics = entry.data?.coverage?.relatedTopics || [];
    return subtopics
      .slice()
      .sort((a, b) => b.pageCount - a.pageCount);
  }

  isPrimaryClusterSubtopic(entry: TreeTopicEntry, subtopic: TreeSubtopicNode): boolean {
    return subtopic.parentTopicSlug === entry.topic.parentTopicSlug;
  }

  getPrimaryDirectSubtopic(entry: TreeTopicEntry): TreeSubtopicNode | null {
    const direct = this.getDirectSubtopics(entry);
    const primary = direct.find(subtopic => this.isPrimaryClusterSubtopic(entry, subtopic));
    return primary || null;
  }

  getDirectVariantSubtopics(entry: TreeTopicEntry): TreeSubtopicNode[] {
    return this.getDirectSubtopics(entry)
      .filter(subtopic => !this.isPrimaryClusterSubtopic(entry, subtopic));
  }

  getSubtopicKey(topicSlug: string, section: 'direct' | 'related', subtopicSlug: string): string {
    return `${topicSlug}:${section}:${subtopicSlug}`;
  }

  isSubtopicExpanded(key: string): boolean {
    return this.expandedSubtopics.has(key);
  }

  toggleSubtopic(key: string) {
    if (this.expandedSubtopics.has(key)) {
      this.expandedSubtopics.delete(key);
      return;
    }
    this.expandedSubtopics.add(key);
    if (!this.mentionDisplayCount.has(key)) this.mentionDisplayCount.set(key, 2);
  }

  getVisibleCorePages(subtopic: TreeSubtopicNode): DiagnosticTopicUrl[] {
    // Always show all core pages for expanded subtopics.
    return subtopic.corePages;
  }

  getVisibleMentionPages(subtopic: TreeSubtopicNode, key: string): DiagnosticTopicUrl[] {
    const limit = this.mentionDisplayCount.get(key) ?? 2;
    return subtopic.mentionPages.slice(0, limit);
  }

  showMoreMentions(key: string, total: number) {
    // One click reveals all mention pages.
    this.mentionDisplayCount.set(key, total);
  }

  hasMoreMentionsVisible(subtopic: TreeSubtopicNode, key: string): boolean {
    return (this.mentionDisplayCount.get(key) ?? 2) < subtopic.mentionPages.length;
  }

  getRemainingMentionCount(subtopic: TreeSubtopicNode, key: string): number {
    const visible = this.mentionDisplayCount.get(key) ?? 2;
    return Math.max(0, subtopic.mentionPages.length - visible);
  }

  formatTreeScore(value: number | undefined | null): string {
    if (!Number.isFinite(value)) return 'n/a';
    return `${((value as number) * 100).toFixed(1)}%`;
  }

  setTreeTagLabelMode(mode: 'variant' | 'canonical') {
    this.treeTagLabelMode = mode;
  }

  getTreeTagLabel(tag: { topicSlug: string; label: string }): string {
    if (this.treeTagLabelMode === 'variant') {
      return tag.label;
    }

    const canonical = this.topics.find(topic => topic.parentTopicSlug === tag.topicSlug)?.label;
    if (canonical && canonical.trim().length > 0) {
      return canonical;
    }

    return this.humanizeSlug(tag.topicSlug);
  }

  private humanizeSlug(value: string): string {
    return value
      .split('-')
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private getTreeEligibleTopics(): ParentTopic[] {
    const actionable = this.topics
      .filter(topic => {
        const hasAuthority = topic.authorityScore > 30;
        const hasValidTier = topic.topicTier && ['owned', 'aspirational', 'adjacent'].includes(topic.topicTier);
        return hasAuthority && hasValidTier;
      })
      .sort((a, b) => b.authorityScore - a.authorityScore);

    // Fallback to top authority topics if a domain has no actionable tiered topics yet.
    if (actionable.length === 0) {
      return this.topics
        .slice()
        .sort((a, b) => b.authorityScore - a.authorityScore);
    }
    return actionable;
  }

  // ─── Share Link ──────────────────────────────────────────────

  /**
   * Create a public share link for the current domain overview
   */
  createShareLink() {
    if (!this.domain.trim() || this.shareLoading) return;

    this.shareLoading = true;
    this.shareUrl = null;
    this.shareCopied = false;
    this.shareError = null;

    this.http.post<{ success: boolean; data: { token: string } }>(`${this.API_URL}/share`, {
      resourceType: 'domain-overview',
      resourceId: this.domain.trim(),
      label: `Topic Authority — ${this.domain}`,
    }).subscribe({
      next: (res) => {
        this.shareLoading = false;
        if (res.success && res.data?.token) {
          this.shareUrl = `${window.location.origin}/share/${res.data.token}`;
        } else {
          this.shareError = 'Failed to create share link';
        }
      },
      error: (err) => {
        this.shareLoading = false;
        this.shareError = err.error?.message || 'Failed to create share link';
      },
    });
  }

  /**
   * Copy the share URL to the clipboard
   */
  copyShareUrl() {
    if (!this.shareUrl) return;
    navigator.clipboard.writeText(this.shareUrl).then(() => {
      this.shareCopied = true;
      setTimeout(() => (this.shareCopied = false), 2000);
    });
  }

  /**
   * Dismiss the share link inline UI
   */
  dismissShare() {
    this.shareUrl = null;
    this.shareError = null;
    this.shareCopied = false;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}

